import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLang } from '../i18n.jsx';
import { useAuth } from '../auth.jsx';
import { api } from '../api.js';
import ProGate from './ProGate.jsx';
import Dropdown from './Dropdown.jsx';
import { useAvisos } from '../avisos.jsx';
import { cardArt, cardArtForCanvas } from '../cardArt.js';
import Arte from './Arte.jsx';
import { useCierreSuave, useVolverCierra } from '../movimiento.js';

/**
 * Hoja del mazo: las 60 cartas con su cantidad, agrupadas por categoria.
 *
 * Pensada para CAPTURAR DE UNA VEZ, en PC o movil: la forma mas rapida de
 * llevarse un mazo a una tienda es un pantallazo, y un pantallazo no puede
 * incluir lo que queda fuera de la pantalla. Por eso el tamaño de las cartas no
 * es fijo: se calcula para que TODO quepa sin desplazamiento.
 *
 * La cantidad va DEBAJO de la carta. Encima tapaba el nombre impreso, que es
 * justo por donde se reconoce la carta en el mostrador.
 *
 * ESTO ES UNA LISTA REAL, NO EL PROMEDIO DEL ARQUETIPO
 *
 * Es la lista de un jugador concreto -- el mejor clasificado del periodo -- y no
 * una media de inclusiones, porque una media no suma 60 ni seria legal, y el
 * objetivo de esta pantalla es poder llevarse el mazo a comprar.
 *
 * La consecuencia confundia: al lado del detalle del arquetipo, que si es el
 * agregado de todas las listas, parecia que una de las dos vistas mentia. Salen
 * cartas que casi nadie juega (elecciones de ESE jugador) y faltan otras que
 * juega casi todo el mundo. Por eso ahora se cruza con el agregado y se marca el
 * porcentaje de inclusion de cada carta: la diferencia deja de leerse como una
 * contradiccion y pasa a ser lo mas informativo de la pantalla.
 */

const ASPECT = 245 / 342;          // ancho / alto de una carta
const GAP = 8;
const CAPTION = 22;                // linea de cantidad e inclusion en UNA linea
/*
 * Por debajo de este ancho, "4× · 99%" no cabe en el ancho de la carta y pasa a
 * dos lineas. El valor sale de medir: 14 px de cantidad mas 11,5 de porcentaje
 * mas el separador ocupan unos 56 px, y la carta puede bajar hasta 34 px en un
 * telefono. Antes se forzaba `nowrap` y el texto se salia por los dos lados,
 * chocando con las cartas vecinas.
 */
const ANCHO_UNA_LINEA = 58;
const CAPTION_DOS = 32;            // la misma linea partida en dos
const GROUP_HEAD = 24;

/**
 * Por debajo de esto, una carta es eleccion del jugador y no del arquetipo.
 * Por encima del otro umbral, es nucleo y que falte dice algo.
 */
const TECH_MAX = 0.25;
const CORE_MIN = 0.5;

/** Inclusion en formato corto: en la hoja el espacio bajo la carta es una linea. */
const pctShort = (n) => `${Math.round(n * 100)}%`;

const GROUPS = [
  ['pokemon', 'Pokémon'],
  ['trainer', 'Trainer'],
  ['energy', 'Energy'],
];

/**
 * Busca el numero de columnas que permite las cartas MAS GRANDES cabiendo
 * entero en el area disponible.
 *
 * Mas columnas = cartas mas estrechas pero menos filas. El optimo no es
 * monotono, asi que se prueban todas y se elige la mejor en vez de estimar.
 */
function bestLayout(counts, width, height) {
  let best = null;
  for (let cols = 4; cols <= 20; cols++) {
    const cardW = (width - (cols + 1) * GAP) / cols;
    if (cardW < 34) continue;                   // ilegible, no vale la pena
    const cardH = cardW / ASPECT;
    const rows = counts.reduce((n, c) => n + Math.ceil(c / cols), 0);
    // El hueco del texto depende del ancho: estrecho va en dos lineas y ocupa
    // mas alto. Si no se contara aqui, el calculo de cuantas caben mentiria.
    const caption = cardW < ANCHO_UNA_LINEA ? CAPTION_DOS : CAPTION;
    const needed =
      counts.length * GROUP_HEAD + rows * (cardH + caption + GAP) + GAP;
    if (needed <= height && cardW > (best?.cardW ?? 0)) best = { cols, cardW };
  }
  if (best) return best;

  /*
   * NINGUN tamaño mete las 60 cartas en el alto disponible: pasa en cualquier
   * telefono en vertical. El respaldo de antes -- 12 columnas de 40 px --
   * ignoraba el ANCHO, y en una pantalla de 360 px la rejilla media 500 y se
   * salia por la derecha, con el final del mazo inalcanzable. Aqui se rinde
   * solo la garantia de "sin desplazamiento": columnas legibles que quepan a
   * lo ancho, y el cuerpo se desplaza en vertical (ver .sheet__body).
   */
  const cols = Math.max(4, Math.floor((width - GAP) / (48 + GAP)));
  return { cols, cardW: Math.max(34, (width - (cols + 1) * GAP) / cols) };
}

/** $12.345 chilenos, sin decimales; los créditos con separador de miles local. */
const fmtClp = (n) =>
  new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(n);

/**
 * `acciones` distingue de DONDE viene el mazo:
 *
 *   - 'meta' (lo de siempre): lista de torneo, con exportar al juego y guardar
 *     en mis mazos -- el cierre del recorrido meta -> juego, con su gating Pro.
 *   - 'simple': un mazo PROPIO o de la comunidad. Esas pantallas ya tienen sus
 *     propios botones de exportar/copiar, y "guardar en mis mazos" un mazo que
 *     ya es tuyo solo fabricaria duplicados. Queda lo que justifica la hoja:
 *     verlo entero y llevarse el PNG.
 *
 * Con `deck.source` en null tampoco se dice "es la lista de Fulano": eso es
 * verdad de una lista de torneo, no de un mazo guardado.
 */
export default function DeckSheet({ deck, inclusion, archetypeName, onOpenCard, onClose, onPickList, acciones = 'meta' }) {
  /*
   * Cambio de lista en curso. El desplegable queda deshabilitado mientras
   * llega la nueva: dos cambios encadenados pintarian el que conteste ultimo,
   * no el ultimo elegido.
   */
  const [cambiando, setCambiando] = useState(false);
  /*
   * Cierre SUAVE, como el resto de modales: la hoja era la unica capa que
   * desaparecia en seco -- entraba con calma y se iba de golpe, justo el
   * contraste que hace sentir barata una interfaz. `pedirCierre` marca
   * `is-closing`, el CSS anima la salida y `onClose` llega al terminar.
   */
  const { ref: refHoja, cerrando, pedirCierre } = useCierreSuave(onClose);
  useVolverCierra(true, pedirCierre);
  const { user } = useAuth();
  const { avisar } = useAvisos();
  const { t } = useLang();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const [layout, setLayout] = useState({ cols: 10, cardW: 74 });
  const bodyRef = useRef(null);
  const headRef = useRef(null);
  const canvasRef = useRef(null);

  /*
   * EXPORTAR EL MAZO DEL META (Pro): al portapapeles en el formato que PTCGL
   * importa, o directo a "mis mazos". Es el cierre del recorrido meta -> juego:
   * ver que domina, llevartelo, y jugarlo -- o rastrear tus partidas con el.
   *
   * El gating se decide AQUI solo para la interfaz; el servidor vuelve a
   * decidirlo (limite de mazos, sesion) porque el cliente siempre se puede
   * manipular. `user.plan` ya llega liquidado (bonus incluido) del servidor.
   */
  const [exportando, setExportando] = useState(null); // null | 'copiado' | 'guardado' | 'error'
  /*
   * Texto exportado que NO llego al portapapeles. El portapapeles puede negarse
   * (permisos, contexto sin gesto, http plano): en ese caso el texto se enseña
   * en un area para copiarlo a mano en vez de un "error" que no lleva a nada.
   * Lo descubrio la verificacion automatizada, donde clipboard siempre se niega.
   */
  const [textoExport, setTextoExport] = useState(null);
  const [gate, setGate] = useState(false);
  const esPro = user?.plan === 'pro';

  const entradas = () =>
    GROUPS.flatMap(([k]) => deck.groups[k] ?? [])
      .filter((c) => c.cardId)
      .map((c) => ({ cardId: c.cardId, count: c.count }));

  const copiarParaElJuego = async () => {
    if (!esPro) return setGate(true);
    try {
      const r = await api('/api/deck/export?format=json', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entries: entradas() }),
      });
      const j = await r.json();
      if (!j?.text) throw new Error('sin texto');
      try {
        await navigator.clipboard.writeText(j.text);
        setExportando('copiado');
        avisar('ok', t('copied'));
      } catch {
        // El portapapeles se nego: el texto se enseña para copiar a mano.
        setTextoExport(j.text);
      }
    } catch {
      setExportando('error');
      avisar('error', t('exportFailed'));
    }
  };

  const guardarEnMisMazos = async () => {
    if (!esPro) return setGate(true);
    if (!user) return setGate(true);
    try {
      const r = await api('/api/decks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: archetypeName, cards: entradas() }),
      });
      if (r.status === 403) return setGate(true);
      if (!r.ok) throw new Error('no guardado');
      setExportando('guardado');
      avisar('ok', t('savedToMyDecks'));
    } catch {
      setExportando('error');
    }
  };

  const groups = GROUPS.filter(([k]) => deck.groups[k]?.length);
  const counts = groups.map(([k]) => deck.groups[k].length);

  /*
   * El orden VISUAL de la hoja, para que la ficha de carta ofrezca las flechas
   * de anterior/siguiente dentro del mazo -- como en la enciclopedia, pero
   * recorriendo estas 60 y no la rejilla de busqueda. Se manda junto al id al
   * abrir la ficha; quien la pinta (App) convierte la lista en las flechas.
   */
  const idsNavegables = useMemo(
    () =>
      GROUPS.flatMap(([k]) => deck.groups[k] ?? [])
        .filter((c) => c.cardId)
        .map((c) => c.cardId),
    [deck]
  );

  /** nombre -> cuota de listas del arquetipo que llevan esa carta. */
  const incByName = useMemo(() => {
    const m = new Map();
    for (const c of inclusion?.cards ?? []) m.set(c.name, c.inclusion);
    return m;
  }, [inclusion]);

  /**
   * Nucleo del arquetipo que ESTA lista deja fuera.
   *
   * Suele ser lo mas revelador de la comparacion: que un mazo renuncie a una
   * carta que juega el 83 % del campo es una decision, no un descuido.
   *
   * Va en la cabecera y no en el cuerpo a proposito: el cuerpo esta dimensionado
   * para que las 60 cartas quepan sin desplazamiento, y meterle una seccion mas
   * romperia justo la garantia que hace util esta pantalla. La cabecera si se
   * mide (`headRef`), asi que el calculo se ajusta solo.
   */
  /** Cuantas ilustraciones son de otra impresion, para avisarlo una sola vez. */
  const swappedCount = useMemo(
    () => GROUPS.flatMap(([k]) => deck.groups[k] ?? []).filter((c) => c.substitutedArt).length,
    [deck]
  );

  const missingCore = useMemo(() => {
    if (!inclusion?.cards?.length) return [];
    const present = new Set(
      GROUPS.flatMap(([k]) => deck.groups[k] ?? []).map((c) => c.name)
    );
    return inclusion.cards
      .filter((c) => c.inclusion >= CORE_MIN && !present.has(c.name))
      .slice(0, 6);
  }, [inclusion, deck]);

  /**
   * El alto disponible se deduce de la VENTANA, no del elemento.
   *
   * Medir el contenedor seria circular: su alto depende del tamaño de carta que
   * estamos calculando, asi que la hoja no podria encogerse hasta su contenido
   * sin entrar en bucle. Partiendo de la ventana, la hoja queda ceñida y no deja
   * espacio muerto abajo, que en una captura es ruido.
   */
  const measure = useCallback(() => {
    const el = bodyRef.current;
    if (!el) return;
    const chrome = 24 + 28 + (headRef.current?.offsetHeight ?? 44) + 16;
    setLayout(bestLayout(counts, el.clientWidth, window.innerHeight - chrome));
  }, [counts.join(',')]);   // eslint-disable-line react-hooks/exhaustive-deps

  // useLayoutEffect: se mide y se aplica antes de pintar, para que no se vea
  // un fotograma con el tamaño por defecto y luego el salto al correcto.
  useLayoutEffect(measure, [measure]);
  useEffect(() => {
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure]);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && pedirCierre();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [pedirCierre]);

  const loadImage = (src) =>
    new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = src;
    });

  /**
   * PNG con la MISMA disposicion que se ve en pantalla, para que descargar y
   * capturar den el mismo resultado y no haya que elegir.
   */
  const downloadPng = async () => {
    setBusy(true);
    setError(false);
    try {
      const S = 3;                       // escala: cartas nitidas en el PNG
      const cols = layout.cols;
      const cardW = Math.round(layout.cardW * S);
      const cardH = Math.round(cardW / ASPECT);
      const gap = GAP * S;
      /*
       * El PNG usa el MISMO criterio que la pantalla: si la carta es estrecha,
       * la cifra y el porcentaje van en dos lineas y hay que reservar mas alto.
       * Si aqui se dejara el valor de una linea mientras la pantalla usa dos, el
       * PNG y la captura dejarian de decir lo mismo -- que es justo lo que esta
       * hoja promete.
       */
      const dosLineas = layout.cardW < ANCHO_UNA_LINEA;
      const caption = (dosLineas ? CAPTION_DOS : CAPTION) * S;
      const head = GROUP_HEAD * S;
      const top = 78 * S;

      const rows = counts.reduce((n, c) => n + Math.ceil(c / cols), 0);
      const width = cols * cardW + (cols + 1) * gap;
      const height = top + groups.length * head + rows * (cardH + caption + gap) + gap;

      const canvas = canvasRef.current ?? document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');

      ctx.fillStyle = '#12141a';
      ctx.fillRect(0, 0, width, height);
      ctx.fillStyle = '#f0b429';
      ctx.font = `bold ${26 * S}px system-ui, sans-serif`;
      ctx.fillText(archetypeName, gap, 34 * S);
      ctx.fillStyle = '#949cb0';
      ctx.font = `${14 * S}px system-ui, sans-serif`;
      ctx.fillText(
        `${deck.total} ${t('cards')}${deck.source?.player ? ` · ${deck.source.player}` : ''}`,
        gap,
        56 * S
      );

      let y = top;
      for (const [key, label] of groups) {
        const cards = deck.groups[key];
        ctx.fillStyle = '#e8eaf0';
        ctx.font = `bold ${14 * S}px system-ui, sans-serif`;
        ctx.fillText(`${label} · ${cards.reduce((s, c) => s + c.count, 0)}`, gap, y + 14 * S);
        y += head;

        for (let i = 0; i < cards.length; i++) {
          const c = cards[i];
          const x = gap + (i % cols) * (cardW + gap);
          const cy = y + Math.floor(i / cols) * (cardH + caption + gap);

          // Por el proxy de la API: si no, las cartas de pokemontcg.io manchan
          // el canvas y la exportacion entera falla.
          const src = cardArtForCanvas(c, 'high');
          const img = src ? await loadImage(src) : null;
          if (img) {
            ctx.drawImage(img, x, cy, cardW, cardH);
            // Misma marca que en pantalla: el PNG tiene que decir lo mismo.
            if (c.substitutedArt) {
              const r = 7 * S;
              ctx.beginPath();
              ctx.arc(x + cardW - r - 2 * S, cy + r + 2 * S, r, 0, Math.PI * 2);
              ctx.fillStyle = 'rgba(0,0,0,.72)';
              ctx.fill();
              ctx.fillStyle = '#d8c07a';
              ctx.font = `bold ${11 * S}px system-ui, sans-serif`;
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';
              ctx.fillText('≈', x + cardW - r - 2 * S, cy + r + 2 * S);
              ctx.textBaseline = 'alphabetic';
              ctx.textAlign = 'left';
            }
          } else {
            ctx.fillStyle = '#1f2330';
            ctx.fillRect(x, cy, cardW, cardH);
            ctx.fillStyle = '#949cb0';
            ctx.font = `${11 * S}px system-ui, sans-serif`;
            ctx.textAlign = 'center';
            ctx.fillText(c.name.slice(0, 16), x + cardW / 2, cy + cardH / 2);
            ctx.textAlign = 'left';
          }

          // Cantidad bajo la carta: encima tapaba el nombre impreso. Con la
          // inclusion al lado, porque el PNG tiene que decir lo mismo que la
          // pantalla -- si no, descargar y capturar dejarian de ser
          // intercambiables, que es lo que promete esta hoja.
          const inc = incByName.get(c.name);
          ctx.font = `bold ${15 * S}px system-ui, sans-serif`;
          ctx.textAlign = 'center';
          if (inc == null) {
            ctx.fillStyle = '#f0b429';
            ctx.fillText(`${c.count}×`, x + cardW / 2, cy + cardH + 16 * S);
          } else {
            // Dos colores en una linea: se mide cada trozo para centrar el
            // conjunto, porque `textAlign` solo centra lo que se pinta de una vez.
            const left = `${c.count}×`;
            if (dosLineas) {
              // Estrecho: una debajo de otra y centradas, sin el separador.
              ctx.textAlign = 'center';
              ctx.fillStyle = '#f0b429';
              ctx.fillText(left, x + cardW / 2, cy + cardH + 14 * S);
              ctx.font = `bold ${12 * S}px system-ui, sans-serif`;
              ctx.fillStyle = inc < TECH_MAX ? '#7ad1a6' : '#949cb0';
              ctx.fillText(pctShort(inc), x + cardW / 2, cy + cardH + 27 * S);
            } else {
              const right = ` · ${pctShort(inc)}`;
              ctx.textAlign = 'left';
              const wl = ctx.measureText(left).width;
              const wr = ctx.measureText(right).width;
              const x0 = x + cardW / 2 - (wl + wr) / 2;
              ctx.fillStyle = '#f0b429';
              ctx.fillText(left, x0, cy + cardH + 16 * S);
              ctx.fillStyle = inc < TECH_MAX ? '#7ad1a6' : '#949cb0';
              ctx.fillText(right, x0 + wl, cy + cardH + 16 * S);
            }
          }
          ctx.textAlign = 'left';
        }
        y += Math.ceil(cards.length / cols) * (cardH + caption + gap);
      }

      const blob = await new Promise((r) => canvas.toBlob(r, 'image/png'));
      if (!blob) throw new Error('sin blob');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${archetypeName.replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '-')}.png`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  };

  const cardStyle = { width: layout.cardW, height: layout.cardW / ASPECT };

  /**
   * Se monta en <body> mediante portal.
   *
   * Un ancestro con `transform` -- y las vistas lo llevan por su animacion de
   * entrada -- se convierte en el bloque contenedor de los hijos `position:
   * fixed`. Sin el portal, este overlay se dimensionaba respecto a la vista del
   * meta (casi 3000 px de alto) en vez de respecto a la ventana, y la hoja
   * dejaba de caber en pantalla, que es justo lo que tiene que garantizar.
   */
  return createPortal(
    <div className={`overlay overlay--sheet${cerrando ? ' is-closing' : ''}`} onClick={pedirCierre}>
      <div className="sheet" ref={refHoja} onClick={(e) => e.stopPropagation()}>
        {/*
          El aspa va en la ESQUINA, no en la fila de acciones.
          Dentro de `.sheet__actions` se leia como un segundo boton pegado a
          "Descargar PNG" -- dos cosas de peso muy distinto, una al lado de otra.
          En el resto de modales de la aplicacion (ficha de carta, cuenta, aviso
          de Pro) el aspa esta arriba a la derecha, y esta hoja era la unica que
          rompia esa costumbre.
        */}
        <button
          type="button"
          className="detail__close sheet__close"
          onClick={pedirCierre}
          aria-label={t('close')}
        >
          ×
        </button>
        <header className="sheet__head" ref={headRef}>
          <div className="sheet__title">
            <h2>{archetypeName}</h2>
            <p className="muted">
              {deck.total} {t('cards')}
              {/* Que es UNA lista y no el promedio va aqui, no en una nota al
                  pie: es lo que explica por que difiere del detalle. Solo con
                  `source`: un mazo propio o de la comunidad no salio de un
                  torneo y esa aclaracion seria mentira. */}
              {(deck.source || (deck.options?.length > 1 && onPickList)) && (
                <>
                  {' · '}
                  <strong className="sheet__src">
                    {deck.options?.length > 1 && onPickList
                      ? t('sheetPickList')
                      : deck.source?.player
                        ? t('sheetIsOneList', { player: deck.source.player })
                        : t('sheetIsOneListPlain')}
                  </strong>
                </>
              )}
              {!(deck.options?.length > 1 && onPickList) && deck.source?.placing != null && (
                <> · {t('sheetPlacing', { n: deck.source.placing })}</>
              )}
              {incByName.size > 0 && <> · {t('openCardHint')}</>}
            </p>
            {/*
              CUAL de las listas del arquetipo se esta viendo. Antes se servia
              la mejor clasificada y las demas ni se sabia que existian; el
              selector vive en la misma linea que ya explicaba de quien era la
              lista -- un control donde antes habia un dato, nada mas sucio.
            */}
            {deck.options?.length > 1 && onPickList && (
              <div className="sheet__listas">
                <Dropdown
                  label=""
                  value={`${deck.source.tournamentId}|${deck.source.player}`}
                  placeholder=""
                  onChange={async (v) => {
                    if (v == null || cambiando) return;
                    const opt = deck.options.find((o) => `${o.tournamentId}|${o.player}` === v);
                    if (
                      !opt ||
                      (opt.tournamentId === deck.source.tournamentId &&
                        opt.player === deck.source.player)
                    ) {
                      return;
                    }
                    setCambiando(true);
                    try {
                      await onPickList(opt);
                    } finally {
                      setCambiando(false);
                    }
                  }}
                  options={deck.options.map((o) => ({
                    key: `${o.tournamentId}|${o.player}`,
                    label: `${o.placing != null ? `${t('sheetPlacing', { n: o.placing })} · ` : ''}${o.player} — ${o.tournament}`,
                  }))}
                />
                {cambiando && <span className="muted sheet__cambiando">{t('loading')}</span>}
              </div>
            )}
            {swappedCount > 0 && (
              <p className="sheet__swapnote">
                <span aria-hidden="true">≈</span> {t('substitutedArtNote', { n: swappedCount })}
              </p>
            )}
            {missingCore.length > 0 && (
              <p className="sheet__missing" title={t('coreMissingHint')}>
                <span className="sheet__missingLabel">{t('coreMissing')}:</span>{' '}
                {missingCore.map((c, i) => (
                  <span key={c.name}>
                    {i > 0 && ', '}
                    {c.name} <span className="muted">({pctShort(c.inclusion)})</span>
                  </span>
                ))}
              </p>
            )}
          </div>
          <div className="sheet__actions">
            <div className="sheet__botones">
              <button type="button" className="btn" onClick={downloadPng} disabled={busy}>
                {busy ? t('generating') : t('downloadPng')}
              </button>
              {acciones === 'meta' && (
                <>
                  <button type="button" className="btn btn--ghost" onClick={copiarParaElJuego}>
                    {exportando === 'copiado' ? t('copied') : t('exportToGame')}
                  </button>
                  <button type="button" className="btn btn--ghost" onClick={guardarEnMisMazos}>
                    {exportando === 'guardado' ? t('savedToDecks') : t('saveToMyDecks')}
                  </button>
                </>
              )}
            </div>
            {/*
              Cuanto cuesta la lista, bajo los botones: es el hueco muerto de la
              cabecera (el bloque del titulo es mas alto), asi que anunciarlo
              aqui no le quita ni un pixel a la rejilla del mazo. El detalle de
              cada aproximacion vive en el title, no en la linea.
            */}
            {(deck.costs?.credits?.total > 0 || deck.costs?.clp?.total > 0) && (
              <p className="sheet__costes">
                {deck.costs?.credits?.total > 0 && (
                  <span
                    title={
                      t('sheetCraftHint') +
                      (deck.costs.credits.unknownCards > 0
                        ? ` ${t('sheetCraftUnknown', { n: deck.costs.credits.unknownCards })}`
                        : '')
                    }
                  >
                    {t('sheetCraft')}{' '}
                    <strong>{t('sheetCraftValue', { n: deck.costs.credits.total.toLocaleString('es-CL') })}</strong>
                  </span>
                )}
                {deck.costs?.credits?.total > 0 && deck.costs?.clp?.total > 0 && ' · '}
                {deck.costs?.clp?.total > 0 && (
                  <span
                    title={
                      t('sheetBuyHint') +
                      (deck.costs.clp.missingCards > 0
                        ? ` ${t('sheetBuyMissing', { n: deck.costs.clp.missingCards })}`
                        : '') +
                      /*
                        Las cotizadas con un precio de marketplace: son un
                        precio real y SI se suman, pero el minimo mezcla
                        idiomas y condiciones, asi que el total tira a la baja.
                      */
                      (deck.costs.clp.aggregateCards > 0
                        ? ` ${t('sheetBuyAggregate', { n: deck.costs.clp.aggregateCards })}`
                        : '')
                    }
                  >
                    {t('sheetBuy')} <strong>≈ {fmtClp(deck.costs.clp.total)}</strong>
                  </span>
                )}
              </p>
            )}
          </div>
        </header>

        {error && <p className="deck__msg is-error">{t('pngFailed')}</p>}
        {exportando === 'error' && <p className="deck__msg is-error">{t('exportFailed')}</p>}
        {textoExport && (
          <div className="sheet__exportManual">
            <p className="muted">{t('copyManually')}</p>
            <textarea
              readOnly
              rows={Math.min(14, textoExport.split(String.fromCharCode(10)).length + 1)}
              value={textoExport}
              onFocus={(e) => e.target.select()}
            />
          </div>
        )}
        <ProGate open={gate} onClose={() => setGate(false)} feature="metaExport" />

        <div className="sheet__body" ref={bodyRef}>
          {groups.map(([key, label]) => (
            <section key={key}>
              <h3>
                {label}
                <span className="muted"> · {deck.groups[key].reduce((s, c) => s + c.count, 0)}</span>
              </h3>
              <ul
                className={
                  'sheet__grid' + (layout.cardW < ANCHO_UNA_LINEA ? ' is-narrow' : '')
                }
              >
                {deck.groups[key].map((c, i) => {
                  const inc = incByName.get(c.name);
                  const isTech = inc != null && inc < TECH_MAX;
                  /**
                   * `substitutedArt` avisa de que la ilustracion es de OTRA
                   * impresion de la misma carta. Pasa con las energias basicas:
                   * las listas traen la edicion mas nueva, que a veces no esta
                   * ilustrada en ninguna fuente, y se toma prestada la de otra
                   * edicion -- son intercambiables en juego y para comprarlas da
                   * igual cual sea.
                   *
                   * El servidor llevaba marcandolo desde el principio y la hoja
                   * lo ignoraba, asi que aparecian fotos que no correspondian a
                   * la carta listada sin ninguna pista de por que.
                   */
                  const swapped = !!c.substitutedArt;
                  return (
                    <li key={`${c.name}-${i}`} style={{ width: layout.cardW }}>
                      {/* Boton y no <li> pinchable: abre la ficha de la carta,
                          asi que tiene que alcanzarse con el teclado igual que
                          los demas accesos a la ficha. Sin cardId no hay ficha
                          que abrir, y entonces no debe parecer pulsable. */}
                      <button
                        type="button"
                        className={`sheet__card${isTech ? ' is-tech' : ''}`}
                        disabled={!c.cardId || !onOpenCard}
                        onClick={() => c.cardId && onOpenCard?.(c.cardId, idsNavegables)}
                        title={[
                          c.name,
                          isTech ? t('techPick') : null,
                          inc != null ? t('inclusionOf', { pct: pctShort(inc) }) : null,
                          swapped ? t('substitutedArtHint', { set: c.set ?? '?', n: c.number ?? '?' }) : null,
                        ]
                          .filter(Boolean)
                          .join(' — ')}
                      >
                        {cardArt(c) ? (
                          <span className="sheet__imgwrap">
                            <Arte src={cardArt(c)} alt={c.name} style={cardStyle} loading="lazy" />
                            {/* Marca esquinera: no ocupa alto propio, asi que no
                                toca el calculo que hace caber las 60 cartas. */}
                            {swapped && (
                              <span className="sheet__swap" aria-hidden="true">≈</span>
                            )}
                          </span>
                        ) : (
                          <div className="sheet__noimg" style={cardStyle}>
                            <span>{t('noArt')}</span>
                          </div>
                        )}
                        {/* Cantidad e inclusion comparten linea a proposito: una
                            segunda linea cambiaria CAPTION y las 60 cartas
                            dejarian de caber en una pantalla. */}
                        <span className="sheet__count">
                          {c.count}×
                          {inc != null && (
                            <span className={`sheet__inc${isTech ? ' is-tech' : ''}`}>
                              {/* El separador se oculta por CSS cuando la carta
                                  es estrecha y el porcentaje baja de linea: ahi
                                  el salto ya separa las dos cifras. */}
                              <span className="sheet__sep"> · </span>
                              {pctShort(inc)}
                            </span>
                          )}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
        <canvas ref={canvasRef} style={{ display: 'none' }} />
      </div>
    </div>,
    document.body
  );
}
